import { getComponent } from "../data/catalog";
import type { ComponentDefinition, MapDefinition, Placement, RackDefinition } from "./types";

export interface CapacityTotals {
    readonly componentCount: number;
    readonly powerWatts: number;
    readonly unratedPowerComponents: number;
    readonly vCpus: number;
    readonly memoryGb: number;
    readonly storageGb: number;
    readonly storageIops: number;
    readonly gpuCount: number;
    readonly gpuMemoryGb: number;
    readonly gpuSlices: number;
    readonly forwardingGbps: number;
    readonly inspectionGbps: number;
}

function emptyCapacity(): CapacityTotals {
    return {
        componentCount: 0,
        powerWatts: 0,
        unratedPowerComponents: 0,
        vCpus: 0,
        memoryGb: 0,
        storageGb: 0,
        storageIops: 0,
        gpuCount: 0,
        gpuMemoryGb: 0,
        gpuSlices: 0,
        forwardingGbps: 0,
        inspectionGbps: 0,
    };
}

function addComponent(total: CapacityTotals, component: ComponentDefinition): CapacityTotals {
    const performance = component.performance;
    return {
        componentCount: total.componentCount + 1,
        powerWatts: total.powerWatts + (performance.powerWatts ?? 0),
        unratedPowerComponents: total.unratedPowerComponents + (performance.powerWatts === null ? 1 : 0),
        vCpus: total.vCpus + (performance.vCpus ?? 0),
        memoryGb: total.memoryGb + (performance.memoryGb ?? 0),
        storageGb: total.storageGb + (performance.storageGb ?? 0),
        storageIops: total.storageIops + (performance.storageIops ?? 0),
        gpuCount: total.gpuCount + (performance.gpuCount ?? 0),
        gpuMemoryGb: total.gpuMemoryGb + (performance.gpuMemoryGb ?? 0),
        gpuSlices: total.gpuSlices + (performance.gpuSlices ?? 0),
        forwardingGbps: total.forwardingGbps + (performance.forwardingGbps ?? 0),
        inspectionGbps: total.inspectionGbps + (performance.inspectionGbps ?? 0),
    };
}

function componentsInRack(rack: RackDefinition, placements: readonly Placement[]): readonly ComponentDefinition[] {
    const components: ComponentDefinition[] = [];
    for (const placement of placements) {
        if (placement.rackId !== rack.id) {
            continue;
        }
        const component = getComponent(placement.componentId);
        if (component !== undefined) {
            components.push(component);
        }
    }
    for (const fixed of rack.fixedComponents ?? []) {
        const component = getComponent(fixed.componentId);
        if (component !== undefined) {
            components.push(component);
        }
    }
    return components;
}

export function aggregateCapacity(components: readonly ComponentDefinition[]): CapacityTotals {
    return components.reduce(addComponent, emptyCapacity());
}

export function getRackCapacity(rack: RackDefinition, placements: readonly Placement[]): CapacityTotals {
    return aggregateCapacity(componentsInRack(rack, placements));
}

export function getDatacenterCapacity(map: MapDefinition, placements: readonly Placement[]): CapacityTotals {
    const components = map.racks.flatMap((rack) => componentsInRack(rack, placements));
    return aggregateCapacity(components);
}

export function getAvailabilityZoneCapacity(
    map: MapDefinition,
    placements: readonly Placement[],
    availabilityZoneId: string,
): CapacityTotals {
    const components = map.racks
        .filter((rack) => rack.availabilityZoneId === availabilityZoneId)
        .flatMap((rack) => componentsInRack(rack, placements));
    return aggregateCapacity(components);
}

export function formatPower(watts: number): string {
    return watts >= 1_000 ? `${(watts / 1_000).toLocaleString("en", { maximumFractionDigits: 2 })} kW` : `${watts} W`;
}

export function formatDataCapacity(gigabytes: number): string {
    return gigabytes >= 1_000
        ? `${(gigabytes / 1_000).toLocaleString("en", { maximumFractionDigits: 1 })} TB`
        : `${gigabytes.toLocaleString("en")} GB`;
}

export function formatRate(value: number): string {
    return value >= 1_000
        ? `${(value / 1_000).toLocaleString("en", { maximumFractionDigits: 1 })} Tbps`
        : `${value.toLocaleString("en")} Gbps`;
}

export function formatCount(value: number): string {
    return value.toLocaleString("en");
}
